import { useMemo } from 'react';
import {
  Box,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Tooltip,
  Checkbox,
  Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseIcon from '@mui/icons-material/Close';
import {
  GridContainer,
  GridItem,
  Datepicker,
  Button,
  InformationField,
  ResyncDetails,
} from '../../../../components/shared';
import { LABELS, TEST_IDS, GRID_SIZES, LAYOUT } from '../../constants';
import useStyles from './style';
import SublocationSelector from './SublocationSelector';
import SublocationsPopoverCell from '../SublocationsPopoverCell';
import { formatPostingDate } from '../../utils/glPostingMethods';
import { parseValidDayjs } from '../../../../utils/dayjsDate';

const displayName = 'HeaderInfoCard';

const getReadOnlySublocations = (sublocations = []) =>
  sublocations
    .map((sublocation) => {
      if (typeof sublocation === 'string') {
        const name = String(sublocation).trim();
        if (!name) return null;
        return { id: '', name, endDate: '' };
      }

      const name = String(sublocation?.name ?? '').trim();
      if (!name) return null;
      return {
        id: String(sublocation?.id ?? sublocation?.sublocationId ?? '').trim(),
        name,
        endDate: formatPostingDate(sublocation?.endDate ?? sublocation?.lastPosting?.endDate) || '',
      };
    })
    .filter(Boolean);

const HeaderInfoCard = ({
  filterProps = {},
  dateProps = {},
  sublocationProps = {},
  isReadOnly = false,
  approvedSummary = null,
  isApplyingFeeds = false,
  resyncData = [],
}) => {
  const classes = useStyles();

  const {
    productTypes = [],
    selectedProductType = '',
    onProductTypeChange,
    isProductTypesLoading = false,
    currencies = [],
    selectedCurrencies = [],
    onCurrencyChange,
    isCurrenciesLoading = false,
  } = filterProps;

  const {
    selectedStartDate = null,
    selectedEndDate = null,
    onStartDateChange,
    onEndDateChange,
    isStartDateDisabled = true,
    isEndDateDisabled = true,
    minStartDate = null,
    maxStartDate = null,
    minEndDate = null,
    maxEndDate = null,
    shouldDisableStartDate,
    shouldDisableEndDate,
    dateConstraintHint = '',
    dateConstraintWarnings = [],
    selectedPostingDate = null,
    onPostingDateChange,
  } = dateProps;

  const {
    sublocations = [],
    allSublocations = [],
    selectedSublocations = [],
    onSublocationChange,
    onSublocationSearch,
    sublocationSearchTerm = '',
    onSelectAllSublocations,
    onDeselectAllSublocations,
    onApplySublocations,
    isLoadingSublocations = false,
  } = sublocationProps;

  const safeProductTypes = useMemo(() => (Array.isArray(productTypes) ? productTypes : []), [productTypes]);
  const safeCurrencies = useMemo(() => (Array.isArray(currencies) ? currencies : []), [currencies]);
  const safeSelectedCurrencies = useMemo(
    () => (Array.isArray(selectedCurrencies) ? selectedCurrencies : []),
    [selectedCurrencies],
  );
  const currencyCodeById = useMemo(
    () =>
      safeCurrencies.reduce((acc, currency) => {
        const id = String(currency?.id ?? '').trim();
        const code = String(currency?.code ?? '')
          .trim()
          .toUpperCase();
        if (id && code) {
          acc[id] = code;
        }
        return acc;
      }, {}),
    [safeCurrencies],
  );
  const startDateValue = parseValidDayjs(selectedStartDate, { fallback: null });
  const endDateValue = parseValidDayjs(selectedEndDate, { fallback: null });
  const postingDateValue = parseValidDayjs(selectedPostingDate, { fallback: null });
  const startDefaultCalendarMonth = !isReadOnly ? parseValidDayjs(minStartDate, { fallback: undefined }) : undefined;
  const endDefaultCalendarMonth = !isReadOnly ? parseValidDayjs(minEndDate, { fallback: undefined }) : undefined;
  const showProductType = !isReadOnly;
  const startDateLabelColor = isStartDateDisabled || isReadOnly ? 'text.disabled' : 'text.primary';
  const endDateLabelColor = isEndDateDisabled || isReadOnly ? 'text.disabled' : 'text.primary';

  const isCurrencyDisabled = !selectedProductType || isReadOnly || isCurrenciesLoading;
  const isSublocationDisabled =
    !selectedProductType || safeSelectedCurrencies.length === 0 || isReadOnly || isLoadingSublocations;
  const safeDateConstraintWarnings = useMemo(
    () => (Array.isArray(dateConstraintWarnings) ? dateConstraintWarnings.filter(Boolean) : []),
    [dateConstraintWarnings],
  );

  const selectedCount = Array.isArray(selectedSublocations) ? selectedSublocations.length : 0;
  const applyDisabledReason = useMemo(() => {
    if (isApplyingFeeds) return LABELS.APPLY_DISABLED_FETCHING_FEEDS;
    if (!selectedProductType) return LABELS.APPLY_DISABLED_SELECT_PRODUCT;
    if (safeSelectedCurrencies.length === 0) return LABELS.APPLY_DISABLED_SELECT_CURRENCY;
    if (safeDateConstraintWarnings.length > 0) {
      const [firstWarning] = safeDateConstraintWarnings;
      const firstMessage = String(firstWarning?.message ?? '').trim() || LABELS.APPLY_DISABLED_DATE_MISMATCH;
      if (safeDateConstraintWarnings.length === 1) return firstMessage;
      const additionalCount = safeDateConstraintWarnings.length - 1;
      return `${firstMessage} (+${additionalCount} more warning${additionalCount === 1 ? '' : 's'})`;
    }
    if (dateConstraintHint) return dateConstraintHint;
    if (!selectedStartDate || !selectedEndDate) return LABELS.APPLY_DISABLED_SELECT_WEEK;
    if (isLoadingSublocations) return LABELS.APPLY_DISABLED_LOADING;
    if (selectedCount === 0) return LABELS.APPLY_DISABLED_SELECT_SUBLOCATION;
    return '';
  }, [
    selectedProductType,
    safeSelectedCurrencies.length,
    safeDateConstraintWarnings,
    dateConstraintHint,
    selectedStartDate,
    selectedEndDate,
    isLoadingSublocations,
    selectedCount,
    isApplyingFeeds,
  ]);
  const isApplyDisabled = Boolean(applyDisabledReason) || isApplyingFeeds;

  const readOnlySummary = useMemo(
    () => ({
      createdBy: approvedSummary?.createdBy || LABELS.NOT_AVAILABLE,
      postedWeek: approvedSummary?.postedWeek || LABELS.NOT_AVAILABLE,
      postingDate: approvedSummary?.postingDate || LABELS.NOT_AVAILABLE,
      productType: approvedSummary?.productType || LABELS.NOT_AVAILABLE,
      x3ProductName: approvedSummary?.x3ProductName || approvedSummary?.productType || '',
      selectedSublocations: Array.isArray(approvedSummary?.selectedSublocations)
        ? approvedSummary.selectedSublocations
        : [],
      unselectedSublocations: Array.isArray(approvedSummary?.unselectedSublocations)
        ? approvedSummary.unselectedSublocations
        : [],
    }),
    [
      approvedSummary?.createdBy,
      approvedSummary?.postedWeek,
      approvedSummary?.postingDate,
      approvedSummary?.productType,
      approvedSummary?.x3ProductName,
      approvedSummary?.selectedSublocations,
      approvedSummary?.unselectedSublocations,
    ],
  );
  const selectedSublocationEntries = useMemo(
    () => getReadOnlySublocations(readOnlySummary.selectedSublocations),
    [readOnlySummary.selectedSublocations],
  );
  const unselectedSublocationEntries = useMemo(
    () => getReadOnlySublocations(readOnlySummary.unselectedSublocations),
    [readOnlySummary.unselectedSublocations],
  );
  const normalizedResyncData = useMemo(() => {
    if (!resyncData) return [];
    const values = Array.isArray(resyncData) ? resyncData : Object.values(resyncData);
    return values.flat().filter(Boolean);
  }, [resyncData]);

  return (
    <Box className={classes.headerPanel} component={Paper}>
      <Box className={classes.headerDisclaimer}>
        <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
        <Typography variant="caption" component="span">
          Excluding Standalone Sub-Locations
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <ResyncDetails resyncData={normalizedResyncData} />
        </Box>
      </Box>
      {isReadOnly ? (
        <GridContainer data-testid="info-pad" spacing={2} px={3} py={1} display="flex" justifyContent="flex-start">
          <GridItem xs={12} sm="auto" sx={{ minWidth: { sm: '160px' } }}>
            <InformationField value={LABELS.PRODUCT_TYPE.toUpperCase()} variant="subtitle2" />
            <InformationField value={readOnlySummary.productType} variant="subtitle1" />
          </GridItem>
          <GridItem xs={12} sm="auto" sx={{ minWidth: { sm: '240px' } }}>
            <InformationField value={LABELS.WEEK} variant="subtitle2" />
            <InformationField value={readOnlySummary.postedWeek} variant="subtitle1" />
          </GridItem>
          <GridItem xs={12} sm="auto" sx={{ minWidth: { sm: '190px' } }}>
            <InformationField value={LABELS.POSTING_DATE.toUpperCase()} variant="subtitle2" />
            <InformationField value={readOnlySummary.postingDate} variant="subtitle1" />
          </GridItem>
          <GridItem xs={12} sm="auto" sx={{ minWidth: { sm: '190px' } }}>
            <InformationField value={LABELS.CREATED_BY.toUpperCase()} variant="subtitle2" />
            <InformationField value={readOnlySummary.createdBy} variant="subtitle1" />
          </GridItem>
          <GridItem
            xs={12}
            sm="auto"
            sx={{ minWidth: { sm: '250px' }, width: 'auto', maxWidth: 420, alignSelf: 'flex-start', flex: 'none' }}
          >
            <InformationField value={LABELS.SELECTED_SUBLOCATIONS.toUpperCase()} variant="subtitle2" />
            <SublocationsPopoverCell
              sublocations={selectedSublocationEntries}
              selectedSublocations={selectedSublocationEntries}
              unselectedSublocations={unselectedSublocationEntries}
              x3ProductName={readOnlySummary.x3ProductName}
              valueVariant="details"
            />
          </GridItem>
        </GridContainer>
      ) : (
        <>
          <GridContainer
            spacing={LAYOUT.GRID_SPACING}
            px={LAYOUT.HEADER_PADDING_X}
            pt={LAYOUT.HEADER_PADDING_TOP}
            pb={LAYOUT.HEADER_PADDING_BOTTOM}
            display="flex"
            alignItems="flex-end"
          >
            {showProductType && (
              <GridItem sx={{ width: { xs: '100%', lg: 300 }, minWidth: { lg: 300 }, flex: { lg: 'none' } }}>
                <Typography
                  variant="subtitle2"
                  className={classes.fieldLabel}
                  color={isReadOnly ? 'text.disabled' : 'text.primary'}
                >
                  {LABELS.PRODUCT_TYPE}
                </Typography>
                <FormControl
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={isReadOnly || isProductTypesLoading}
                  className={isReadOnly || isProductTypesLoading ? classes.disabledDropdown : ''}
                >
                  <Select
                    value={selectedProductType ?? ''}
                    onChange={onProductTypeChange}
                    displayEmpty
                    data-testid={TEST_IDS.PRODUCT_TYPE_SELECT}
                    endAdornment={
                      isProductTypesLoading ? (
                        <CircularProgress size={20} sx={{ position: 'absolute', right: 32 }} />
                      ) : null
                    }
                  >
                    <MenuItem value="" disabled>
                      {LABELS.SELECT_PRODUCT}
                    </MenuItem>
                    {safeProductTypes.map((productType) => (
                      <MenuItem key={productType?.id ?? ''} value={String(productType?.id ?? '')}>
                        {productType?.name ?? LABELS.NOT_AVAILABLE}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}

            <GridItem sx={{ width: { xs: '100%', lg: 300 }, minWidth: { lg: 300 }, flex: { lg: 'none' } }}>
              <Typography
                variant="subtitle2"
                className={classes.fieldLabel}
                color={isCurrencyDisabled ? 'text.disabled' : 'text.primary'}
              >
                {LABELS.CURRENCY}
              </Typography>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                disabled={isCurrencyDisabled}
                className={`${isCurrencyDisabled ? classes.disabledDropdown : ''} ${classes.currencyFormControl}`}
              >
                <Select
                  multiple
                  value={safeSelectedCurrencies}
                  onChange={(event) => {
                    onCurrencyChange?.(event?.target?.value ?? []);
                  }}
                  displayEmpty
                  data-testid={TEST_IDS.CURRENCY_SELECT}
                  renderValue={(selected) => {
                    const selectedValues = Array.isArray(selected) ? selected : [];
                    if (!selectedValues.length) {
                      return (
                        <Typography className={classes.currencyPlaceholder}>{LABELS.SELECT_CURRENCIES}</Typography>
                      );
                    }

                    return (
                      <Box className={classes.currencyValueWrap}>
                        {selectedValues.map((currencyId) => {
                          const normalizedCurrencyId = String(currencyId ?? '').trim();
                          const currencyCode = currencyCodeById[normalizedCurrencyId] ?? normalizedCurrencyId;

                          return (
                            <Chip
                              key={normalizedCurrencyId}
                              label={currencyCode}
                              size="small"
                              className={classes.currencyValueChip}
                              deleteIcon={<CloseIcon fontSize="small" />}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onDelete={() => {
                                onCurrencyChange?.(
                                  safeSelectedCurrencies.filter(
                                    (selectedCurrencyId) =>
                                      String(selectedCurrencyId ?? '').trim() !== normalizedCurrencyId,
                                  ),
                                );
                              }}
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                  endAdornment={
                    isCurrenciesLoading ? <CircularProgress size={20} sx={{ position: 'absolute', right: 32 }} /> : null
                  }
                >
                  {safeCurrencies.length === 0 && !isCurrenciesLoading ? (
                    <MenuItem disabled>{LABELS.NO_ACTIVE_CURRENCIES}</MenuItem>
                  ) : (
                    safeCurrencies.map((currency) => (
                      <MenuItem
                        key={currency?.id ?? currency?.code}
                        value={String(currency?.id ?? '')}
                        className={classes.currencyMenuItem}
                      >
                        <Checkbox checked={safeSelectedCurrencies.includes(String(currency?.id ?? ''))} size="small" />
                        {currency?.label ?? LABELS.NOT_AVAILABLE}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </GridItem>

            <SublocationSelector
              sublocations={sublocations}
              allSublocations={allSublocations}
              selectedSublocations={selectedSublocations}
              searchTerm={sublocationSearchTerm}
              onSublocationChange={onSublocationChange}
              onSearchChange={onSublocationSearch}
              onSelectAll={onSelectAllSublocations}
              onDeselectAll={onDeselectAllSublocations}
              isDisabled={isSublocationDisabled}
              isLoading={isLoadingSublocations}
            />
          </GridContainer>

          <GridContainer
            spacing={LAYOUT.GRID_SPACING}
            px={LAYOUT.HEADER_PADDING_X}
            pt={LAYOUT.SUBLOCATION_PADDING_TOP}
            pb={LAYOUT.SUBLOCATION_PADDING_BOTTOM}
            display="flex"
            alignItems="flex-start"
          >
            <GridItem sx={{ width: { xs: '100%', lg: 300 }, minWidth: { lg: 300 }, flex: { lg: 'none' } }}>
              <Typography variant="subtitle2" className={classes.fieldLabel} color={startDateLabelColor}>
                {LABELS.START_DATE}
              </Typography>
              <Box sx={{ pointerEvents: isStartDateDisabled || isReadOnly ? 'none' : 'auto' }}>
                <Datepicker
                  value={startDateValue}
                  onChange={onStartDateChange}
                  disabled={isStartDateDisabled || isReadOnly}
                  size="small"
                  minDate={minStartDate ?? undefined}
                  maxDate={maxStartDate ?? undefined}
                  defaultCalendarMonth={startDefaultCalendarMonth}
                  shouldDisableDate={shouldDisableStartDate}
                  data-testid={TEST_IDS.START_DATE_PICKER}
                />
              </Box>
            </GridItem>

            <GridItem sx={{ width: { xs: '100%', lg: 300 }, minWidth: { lg: 300 }, flex: { lg: 'none' } }}>
              <Typography variant="subtitle2" className={classes.fieldLabel} color={endDateLabelColor}>
                {LABELS.END_DATE}
              </Typography>
              <Box sx={{ pointerEvents: isEndDateDisabled || isReadOnly ? 'none' : 'auto' }}>
                <Datepicker
                  size="small"
                  value={endDateValue}
                  onChange={onEndDateChange}
                  disabled={isEndDateDisabled || isReadOnly}
                  minDate={minEndDate ?? undefined}
                  maxDate={maxEndDate ?? undefined}
                  defaultCalendarMonth={endDefaultCalendarMonth}
                  shouldDisableDate={shouldDisableEndDate}
                  data-testid={TEST_IDS.END_DATE_PICKER}
                />
              </Box>
            </GridItem>

            <GridItem sx={{ width: { xs: '100%', lg: 300 }, minWidth: { lg: 300 }, flex: { lg: 'none' } }}>
              <Typography
                variant="subtitle2"
                className={classes.fieldLabel}
                color={isReadOnly ? 'text.disabled' : 'text.primary'}
              >
                {LABELS.POSTING_DATE}
              </Typography>
              <Box sx={{ pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                <Datepicker
                  size="small"
                  value={postingDateValue}
                  onChange={onPostingDateChange}
                  disabled={isReadOnly}
                  data-testid={TEST_IDS.POSTING_DATE_PICKER}
                />
              </Box>
            </GridItem>

            <GridItem
              {...GRID_SIZES.APPLY_BUTTON}
              sx={{ marginLeft: 'auto', alignSelf: 'flex-end', display: 'flex', justifyContent: 'flex-end' }}
            >
              <Tooltip
                title={applyDisabledReason}
                disableHoverListener={!isApplyDisabled}
                disableFocusListener={!isApplyDisabled}
                disableTouchListener={!isApplyDisabled}
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onApplySublocations}
                    className={classes.applyButton}
                    data-testid={TEST_IDS.APPLY_BUTTON}
                    disabled={isApplyDisabled}
                    startIcon={isApplyingFeeds ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isApplyingFeeds ? LABELS.APPLYING : LABELS.APPLY}
                  </Button>
                </span>
              </Tooltip>
            </GridItem>
          </GridContainer>
          {!safeDateConstraintWarnings.length && dateConstraintHint && (
            <Box px={LAYOUT.HEADER_PADDING_X} pb={1}>
              <Box className={classes.dateConstraintHintPanel}>
                <WarningAmberRoundedIcon className={classes.dateConstraintHintIcon} />
                <Typography variant="body2" className={classes.dateConstraintHintText}>
                  {dateConstraintHint}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

HeaderInfoCard.displayName = displayName;

export default HeaderInfoCard;
